import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { alpha } from "@mui/material/styles";
import LightMode from "@mui/icons-material/LightMode";
import DarkMode from "@mui/icons-material/DarkMode";
import SettingsBrightness from "@mui/icons-material/SettingsBrightness";
import { useTranslation } from "react-i18next";

import {
  setThemePreference,
  useMaterialUIController,
  type ThemePreference,
} from "../../context";

const OPTIONS: Array<{
  value: ThemePreference;
  icon: typeof LightMode;
  labelKey: "configurator.themeLight" | "configurator.themeDark" | "configurator.themeSystem";
}> = [
  { value: "light", icon: LightMode, labelKey: "configurator.themeLight" },
  { value: "dark", icon: DarkMode, labelKey: "configurator.themeDark" },
  { value: "system", icon: SettingsBrightness, labelKey: "configurator.themeSystem" },
];

const CONTROL_HEIGHT = 36;
const TOUCH_HEIGHT = 44;

type ThemeModeToggleProps = {
  /** Larger, evenly spaced icon buttons for narrow screens. */
  touch?: boolean;
};

export default function ThemeModeToggle({ touch = false }: ThemeModeToggleProps) {
  const { t } = useTranslation();
  const [controller, dispatch] = useMaterialUIController();
  const preference = (controller.themePreference as ThemePreference) || "system";
  const buttonHeight = touch ? TOUCH_HEIGHT : CONTROL_HEIGHT;

  return (
    <Box
      role="group"
      aria-label={t("configurator.theme")}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: touch ? 1 : 0.75,
        p: 0.5,
        height: buttonHeight + 8,
        boxSizing: "border-box",
        flexShrink: 0,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: (theme) =>
          theme.palette.mode === "dark"
            ? alpha(theme.palette.common.white, 0.08)
            : theme.palette.grey[100],
      }}
    >
      {OPTIONS.map((option) => {
        const Icon = option.icon;
        const label = t(option.labelKey);
        const selected = preference === option.value;

        return (
          <Tooltip key={option.value} title={label}>
            <Button
              size="small"
              variant={selected ? "contained" : "text"}
              color={selected ? "primary" : "inherit"}
              aria-label={label}
              aria-pressed={selected}
              onClick={() => setThemePreference(dispatch, option.value)}
              startIcon={<Icon sx={{ fontSize: touch ? "22px !important" : "18px !important" }} />}
              sx={{
                minWidth: touch ? TOUCH_HEIGHT : 0,
                width: touch ? TOUCH_HEIGHT : undefined,
                height: buttonHeight,
                px: touch ? 0 : { xs: 1, sm: 1.25 },
                py: 0,
                borderRadius: 1.5,
                textTransform: "none",
                fontWeight: selected ? 700 : 600,
                fontSize: "0.8125rem",
                lineHeight: 1.2,
                letterSpacing: 0,
                boxShadow: "none",
                whiteSpace: "nowrap",
                color: selected ? "primary.contrastText" : "text.secondary",
                bgcolor: selected ? "primary.main" : "transparent",
                "& .MuiButton-startIcon": {
                  m: 0,
                  mr: touch ? 0 : { xs: 0, sm: 0.75 },
                },
                "&:hover": {
                  boxShadow: "none",
                  bgcolor: selected
                    ? "primary.focus"
                    : (theme) =>
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.common.white, 0.12)
                          : theme.palette.action.hover,
                },
              }}
            >
              <Box component="span" sx={{ display: touch ? "none" : { xs: "none", sm: "inline" } }}>
                {label}
              </Box>
            </Button>
          </Tooltip>
        );
      })}
    </Box>
  );
}
